import type { Cell, Column, ColumnType, TypedTable } from './model';

export const cellText = (value: Cell | undefined): string =>
  value == null ? '' : Array.isArray(value) ? value.join(', ') : String(value);

export const optionsFor = (column: Column) =>
  column.options.map(option =>
    typeof option === 'string' ? { id: option, label: option } : option,
  );

/** Empty multi-select arrays must not leak into a differently typed column. */
export function changeEmptyColumnType(
  table: TypedTable,
  id: string,
  type: ColumnType,
  options: Column['options'] = [],
): TypedTable {
  const column = table.columns.find(column => column.id === id);

  if (!column || column.type === type || table.rows.some(row => cellText(row.cells[id]) !== '')) {
    return table;
  }

  return {
    ...table,
    columns: table.columns.map(column =>
      column.id === id ? { ...column, type, options } : column,
    ),
    rows: table.rows.map(row => ({
      ...row,
      cells: Object.fromEntries(Object.entries(row.cells).filter(([key]) => key !== id)),
    })),
  };
}

/** Save the departing cell and append its next row in one editor transaction. */
export function appendTableRow(
  table: TypedTable,
  edit?: { rowId: string; columnId: string; value: Cell },
  id: string = crypto.randomUUID(),
): TypedTable {
  if (table.rows.length >= 10000) {
    throw new Error('This table has reached its 10,000-row limit.');
  }

  if (
    edit &&
    (!table.rows.some(row => row.id === edit.rowId) ||
      !table.columns.some(column => column.id === edit.columnId))
  ) {
    throw new Error('The cell is no longer available.');
  }

  const rows = edit
    ? table.rows.map(row =>
        row.id === edit.rowId
          ? { ...row, cells: { ...row.cells, [edit.columnId]: edit.value } }
          : row,
      )
    : table.rows;

  return { ...table, rows: [...rows, { id, cells: {} }] };
}

/** Column IDs keep cells and view rules attached while only presentation order changes. */
export function moveColumn(table: TypedTable, id: string, to: number): TypedTable {
  const from = table.columns.findIndex(column => column.id === id);

  if (from < 0 || !Number.isInteger(to) || to < 0 || to >= table.columns.length || from === to) {
    return table;
  }

  const columns = [...table.columns];

  columns.splice(to, 0, ...columns.splice(from, 1));

  return { ...table, columns };
}

export function removeColumn(table: TypedTable, id: string): TypedTable {
  if (table.columns.length <= 1 || !table.columns.some(column => column.id === id)) {
    return table;
  }

  return {
    ...table,
    columns: table.columns.filter(column => column.id !== id),
    rows: table.rows.map(row => ({
      ...row,
      cells: Object.fromEntries(Object.entries(row.cells).filter(([key]) => key !== id)),
    })),
    sortColumn: table.sortColumn === id ? '' : table.sortColumn,
    filterColumn: table.filterColumn === id ? '' : table.filterColumn,
    filterValue: table.filterColumn === id ? '' : table.filterValue,
    sorts: table.sorts?.filter(sort => sort.column !== id),
    filters: table.filters?.filter(filter => filter.column !== id),
  };
}

const tableNumberFormat = new Intl.NumberFormat('en-US', {
  useGrouping: true,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function displayCell(column: Column, value: Cell | undefined) {
  if (column.type === 'number' && typeof value === 'number') {
    return tableNumberFormat.format(Object.is(value, -0) ? 0 : value);
  }

  const label = (id: string) => optionsFor(column).find(o => o.id === id)?.label ?? id;

  return ['select', 'multi'].includes(column.type)
    ? Array.isArray(value)
      ? value.map(label).join(', ')
      : value == null
        ? ''
        : label(String(value))
    : cellText(value);
}

export function parseCell(column: Column, value: string | boolean): Cell {
  if (column.type === 'checkbox') {
    return value === true || value === 'true';
  }

  if (String(value).trim() === '') {
    return null;
  }

  if (column.type === 'number') {
    const text = String(value).trim();

    if (!/^[+-]?(?:(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(text)) {
      throw new Error('Enter a valid number, such as 1,234.00');
    }

    const number = Number(text.replaceAll(',', ''));

    if (!Number.isFinite(number)) {
      throw new Error('Enter a valid number');
    }

    return number;
  }

  if (column.type === 'date') {
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(String(value)) ||
      !Number.isFinite(Date.parse(String(value))) ||
      new Date(String(value)).toISOString().slice(0, 10) !== value
    ) {
      throw new Error('Enter a valid date');
    }
  }

  if (column.type === 'url') {
    let url: URL;

    try {
      url = new URL(String(value));
    } catch {
      throw new Error('Enter a complete http or https URL');
    }

    if (!['http:', 'https:'].includes(url.protocol)) {
      throw new Error('Use an http or https URL');
    }
  }

  if (column.type === 'select' || column.type === 'multi') {
    const selected = (column.type === 'multi' ? String(value).split(',') : [String(value)])
      .map(v => v.trim())
      .filter(Boolean)
      .map(v => {
        const option = optionsFor(column).find(o => o.id === v || o.label === v);

        if (!option) {
          throw new Error('Choose an existing option');
        }

        return option.id;
      });

    return column.type === 'select' ? selected[0] : [...new Set(selected)];
  }

  return String(value);
}

export function tableView(table: TypedTable) {
  const filters = [
    ...(table.filters ?? []),
    ...(table.filterColumn
      ? [{ column: table.filterColumn, operator: table.filterOperator, value: table.filterValue }]
      : []),
  ];

  let rows = [...table.rows];

  for (const filter of filters) {
    const column = table.columns.find(c => c.id === filter.column);

    if (!column || (filter.value === '' && filter.operator !== 'empty')) {
      continue;
    }

    rows = rows.filter(row => {
      const value = row.cells[column.id],
        text = displayCell(column, value).toLocaleLowerCase();

      const match = (
        optionsFor(column).find(o => o.id === filter.value)?.label ?? filter.value
      ).toLocaleLowerCase();

      if (filter.operator === 'empty') {
        return value == null || text === '';
      }

      if (column.type === 'number' && ['equals', 'gt', 'lt'].includes(filter.operator)) {
        if (value == null || text === '') {
          return false;
        }

        let target: Cell;

        try {
          target = parseCell(column, filter.value);
        } catch {
          return false;
        }

        if (typeof target !== 'number' || typeof value !== 'number') {
          return false;
        }

        return filter.operator === 'equals'
          ? value === target
          : filter.operator === 'gt'
            ? value > target
            : value < target;
      }

      if (filter.operator === 'equals') {
        return Array.isArray(value)
          ? value.some(v => displayCell(column, v).toLocaleLowerCase() === match)
          : text === match;
      }

      if (filter.operator === 'contains') {
        return (
          text.includes(match) || (column.type === 'number' && cellText(value).includes(match))
        );
      }

      if (value == null || text === '') {
        return false;
      }

      const left = column.type === 'number' ? Number(value) : text,
        right = column.type === 'number' ? Number(match) : match;

      return filter.operator === 'gt' ? left > right : left < right;
    });
  }

  const sorts = [
    ...(table.sorts ?? []),
    ...(table.sortColumn ? [{ column: table.sortColumn, direction: table.sortDirection }] : []),
  ];

  rows.sort((a, b) => {
    for (const rule of sorts) {
      const column = table.columns.find(c => c.id === rule.column);

      if (!column) {
        continue;
      }

      const left = a.cells[column.id],
        right = b.cells[column.id];

      if (left == null || cellText(left) === '') {
        if (right == null || cellText(right) === '') {
          continue;
        }

        return 1;
      }

      if (right == null || cellText(right) === '') {
        return -1;
      }

      const comparison = ['number', 'checkbox'].includes(column.type)
        ? Number(left) - Number(right)
        : displayCell(column, left).localeCompare(displayCell(column, right));

      if (comparison) {
        return rule.direction === 'desc' ? -comparison : comparison;
      }
    }

    return 0;
  });

  return rows;
}
